/*
 * fx-pointer.js — pointer-interaction directives (Nerdio Theme fx pack, 2026-07).
 *
 * Clean-room port of nerdio-astro's motion directives to vanilla theme JS:
 *   - MotionDirectives.astro  runTilt()/runMagnet()  (perspective 800px,
 *     TILT_MAX 6deg, MAGNET_STRENGTH 0.25, mouse-only, WeakSet idempotency)
 *   - BlocksMotion.astro      [data-nn-gradient-follow] delegated pointermove
 *     (one document-level listener, capture-phase pointerleave reset)
 *
 * ONE delegated document-level pointermove/pointerleave pair services three
 * opt-in attributes — no per-element binding, so nothing needs to re-run
 * when the Shopify theme editor adds/reorders/re-renders blocks or sections;
 * delegation covers new DOM automatically.
 *
 *   [data-tilt] [data-tilt-max="8"]
 *       3D card tilt, mouse only. Sets transform: perspective(800px)
 *       rotateX/rotateY (±6deg default, or ±data-tilt-max) and --mx/--my
 *       (cursor position, percent). Resets transform on pointerleave.
 *
 *   [data-cursor-glow]
 *       Cursor-follow glow tracking. Sets --mx/--my (percent) and toggles
 *       --fx-active 1/0 on enter/leave. Consumed by fx-pointer.css's glow
 *       overlay (and any border-glow CSS from other fx packages).
 *
 *   [data-magnet] [data-magnet-strength="0.3"] [data-magnet-pad="90"]
 *       Magnetic hover translate, mouse only. Reworked 2026-07-10 to the
 *       reactbits.dev/animations/magnet model: an activation FIELD extends
 *       data-magnet-pad px (default 90) beyond the element, so the button
 *       starts drifting toward the cursor on APPROACH, tracks it while
 *       inside the field (translate = offset-from-untransformed-center *
 *       strength), and springs back with a slower ease when the cursor
 *       exits the field. Center is derived from the untransformed position
 *       (rect minus current offset) so tracking can't feedback-jitter.
 *       Unlike tilt/glow this iterates a cached element list per move —
 *       field activation can't be event-delegated (the pointer is outside
 *       the element when the pull should begin). Cache invalidates on
 *       theme-editor events and every few seconds (cheap requery).
 *
 * No-ops entirely under prefers-reduced-motion: reduce (no listeners
 * attached at all). Idempotent via the window.__fxPointer guard, so the
 * <script> tag can be safely emitted from multiple block instances on the
 * same page (Shopify has no shared "load once" mechanism for block-owned
 * assets). Zero rAF — every update happens synchronously inside the pointer
 * event, matching the reference (no idle-loop cost).
 */
(function () {
  'use strict';

  if (window.__fxPointer) return;
  window.__fxPointer = true;

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  var TILT_MAX = 6; // degrees — restrained, matches the Astro reference
  var MAGNET_STRENGTH = 0.25;
  var MAGNET_PAD = 90; // px activation field beyond the element (reactbits Magnet model)

  function closest(el, selector) {
    return el && el.closest ? el.closest(selector) : null;
  }

  // ---- magnet element cache (field activation needs a list, not delegation)
  var magnetList = null;
  var magnetListAt = 0;
  var magnetState = new WeakMap(); // el -> { x, y, active }
  function magnets() {
    var now = Date.now();
    if (!magnetList || now - magnetListAt > 3000) {
      magnetList = [].slice.call(document.querySelectorAll('[data-magnet]'));
      magnetListAt = now;
    }
    return magnetList;
  }
  function invalidateMagnets() { magnetList = null; }
  document.addEventListener('shopify:section:load', invalidateMagnets);
  document.addEventListener('shopify:block:select', invalidateMagnets);

  function releaseMagnet(el, st) {
    st.active = false;
    st.x = 0;
    st.y = 0;
    el.style.transition = 'transform 0.5s ease-in-out';
    // Clear (not identity) so CSS-driven hover transforms restore.
    el.style.transform = '';
  }

  document.addEventListener(
    'pointermove',
    function (e) {
      var isMouse = e.pointerType === 'mouse';

      if (isMouse) {
        var tiltEl = closest(e.target, '[data-tilt]');
        if (tiltEl) {
          var tRect = tiltEl.getBoundingClientRect();
          var tx = (e.clientX - tRect.left) / tRect.width;
          var ty = (e.clientY - tRect.top) / tRect.height;
          var max = parseFloat(tiltEl.dataset.tiltMax) || TILT_MAX;
          var rx = (0.5 - ty) * max;
          var ry = (tx - 0.5) * max;
          tiltEl.style.transform = 'perspective(800px) rotateX(' + rx + 'deg) rotateY(' + ry + 'deg)';
          tiltEl.style.setProperty('--mx', tx * 100 + '%');
          tiltEl.style.setProperty('--my', ty * 100 + '%');
        }
      }

      var glowEl = closest(e.target, '[data-cursor-glow]');
      if (glowEl) {
        var gRect = glowEl.getBoundingClientRect();
        var gx = ((e.clientX - gRect.left) / gRect.width) * 100;
        var gy = ((e.clientY - gRect.top) / gRect.height) * 100;
        glowEl.style.setProperty('--mx', gx + '%');
        glowEl.style.setProperty('--my', gy + '%');
        glowEl.style.setProperty('--fx-active', '1');
      }

      if (isMouse) {
        var list = magnets();
        for (var i = 0; i < list.length; i++) {
          var el = list[i];
          var st = magnetState.get(el);
          if (!st) { st = { x: 0, y: 0, active: false }; magnetState.set(el, st); }
          var r = el.getBoundingClientRect();
          if (r.width === 0 && r.height === 0) continue; // hidden
          // Untransformed center: current rect minus the offset we applied.
          var cx = r.left + r.width / 2 - st.x;
          var cy = r.top + r.height / 2 - st.y;
          var dx = e.clientX - cx;
          var dy = e.clientY - cy;
          var pad = parseFloat(el.dataset.magnetPad) || MAGNET_PAD;
          var inField = Math.abs(dx) < r.width / 2 + pad && Math.abs(dy) < r.height / 2 + pad;
          if (inField) {
            var strength = parseFloat(el.dataset.magnetStrength) || MAGNET_STRENGTH;
            st.x = dx * strength;
            st.y = dy * strength;
            if (!st.active) {
              st.active = true;
              el.style.transition = 'transform 0.3s ease-out';
            }
            el.style.transform = 'translate3d(' + st.x.toFixed(1) + 'px, ' + st.y.toFixed(1) + 'px, 0)';
          } else if (st.active) {
            releaseMagnet(el, st);
          }
        }
      }
    },
    { passive: true }
  );

  // pointerleave doesn't bubble, but a capture-phase document listener still
  // sees it on the way down to the target — same trick BlocksMotion.astro
  // uses for [data-nn-gradient-follow].
  document.addEventListener(
    'pointerleave',
    function (e) {
      // Clear the inline transform (rather than writing an explicit identity
      // matrix) so an element that ALSO carries a CSS-driven hover transform
      // (e.g. this theme's Advanced Styles "Hover Effect" system) gets that
      // rule back once the pointer-tracked interaction stops.
      var tiltEl = closest(e.target, '[data-tilt]');
      if (tiltEl) tiltEl.style.transform = '';

      var glowEl = closest(e.target, '[data-cursor-glow]');
      if (glowEl) glowEl.style.setProperty('--fx-active', '0');

      // Window-exit safety: release any magnet still pulled (the field logic
      // in pointermove can't run once the pointer has left the document).
      if (e.target === document.documentElement || e.target === document.body) {
        var list = magnetList || [];
        for (var i = 0; i < list.length; i++) {
          var st = magnetState.get(list[i]);
          if (st && st.active) releaseMagnet(list[i], st);
        }
      }
    },
    { passive: true, capture: true }
  );
})();
